import React, { useEffect, useRef, useState } from "react";
import "./viewClientQuoteModal.css";
import { useMutation } from "@apollo/client";
import {
  CREATE_CLIENT_INVOICE,
  CREATE_PENDING_JOB_FORM,
} from "../../../../utils/GraphQL/mutations";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import EventDateTimeInput from "../EventDateTimeInput/EventDateTimeInput";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import ClientModalInvoiceQuotePage from "../ClientModalInvoiceQuotePage/ClientModalInvoiceQuotePage";
import CycleConfirmationModal from "../CycleConfirmationModal/CycleConfirmationModal";
import PlaceholderCleanupModal from "../PlaceholderCleanupModal/PlaceholderCleanupModal";
import invoiceHTMLToPDFBase64 from "../../../../utils/invoiceHTMLToPDFBase64";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import Switch from "@mui/material/Switch";
import { lockBodyScroll, releaseBodyScroll } from "../../../../utils/bodyScrollLock";
import SpecialRefinisherPayBlock, {
  buildSpecialRefinisherPayInput,
  validateSpecialRefinisherPay,
} from "../../../../components/SpecialRefinisherPayBlock/SpecialRefinisherPayBlock";

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#00e676" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#00e676",
  },
};

const invoiceObj = {
  jobDate: "",
  jobEndTime: "",
  invoicePaid: false,
  invoiceNote: "",
  paidBy: null,
};

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;

const ViewClientQuoteModal = ({
  selectedQuote,
  closeQuoteModal,
  queryClient,
  emailObjHandler,
  changeParentForEmailModal,
}) => {
  const { handleSnack } = useSnackbar();
  const [viewHistory, setViewHistory] = useState([]);
  const [invoice, setInvoice] = useState({ ...invoiceObj });
  const [openInvoiceOptions, setOpenInvoiceOptions] = useState(false);
  const [allowInvoice, setAllowInvoice] = useState(false);
  const [noteLabel, setNoteLabel] = useState(`Invoice Note:  (Optional)`);
  const [dateErr, setDateErr] = useState(false);
  const [createInvoiceCall] = useMutation(CREATE_CLIENT_INVOICE);
  const [createPendingJobFormCall] = useMutation(CREATE_PENDING_JOB_FORM);
  const timeoutIdRef = useRef();
  const [displayQuotePdf, setDisplayQuotePdf] = useState(false);
  const [cyclePromptTaskId, setCyclePromptTaskId] = useState(null);
  const pendingPostCreateRef = useRef(null);

  // Calendar toggle
  const [createCalEvent, setCreateCalEvent] = useState(true);

  // Placeholder cleanup after a quote->invoice graduation that ALSO created a
  // calendar event. pendingFinishRef holds the finishPostCreate args so the
  // queryClient refresh / email composer / close run AFTER the cleanup modal.
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const pendingFinishRef = useRef(null);

  // Special Refinisher Pay override — inherits from selected quote if set there.
  const initialSrp = selectedQuote?.specialRefinisherPay;
  const [srpEnabled, setSrpEnabled] = useState(!!initialSrp?.enabled);
  const [srpAmount, setSrpAmount] = useState(initialSrp?.amount || null);
  const [srpPayType, setSrpPayType] = useState(initialSrp?.payType || null);
  const srpInheritedHint =
    selectedQuote?.specialRefinisherPay?.enabled === true
      ? "inherited from quote"
      : null;

  useEffect(() => {
    if (selectedQuote?.id?.length === 24) {
      if (/invoice/gi?.test(selectedQuote?.quoteStatus) === false) {
        setAllowInvoice(true);
      }

      if (selectedQuote?.quoteViewed?.length > 0) {
        const reverseOrder = [...selectedQuote?.quoteViewed].sort((a, b) => {
          return a?.timestamp > b?.timestamp ? -1 : 1;
        });
        setViewHistory([...reverseOrder]);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuote]);

  /*
    The lock was genuinely UNBALANCED before: it locked when a quote was
    selected, released in the same effect when deselected, AND released again
    in `closeModal` — which itself clears the selection, so one open produced
    two releases. Harmless while it was a raw style write; against a shared
    count it would have stolen a sibling modal's lock.
  */
  const quoteModalOpen = selectedQuote?.id?.length === 24;
  useEffect(() => {
    if (!quoteModalOpen) return undefined;
    lockBodyScroll();
    return () => releaseBodyScroll();
  }, [quoteModalOpen]);

  useEffect(() => {
    const timeoutId = timeoutIdRef.current;
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (displayQuotePdf === true) {
      preparePDF("invoice-to-print");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayQuotePdf]);

  const handleOpenInvoiceOptions = () => {
    if (openInvoiceOptions) {
      setInvoice({ ...invoiceObj });
      setCreateCalEvent(true);
      // Reset SRP state to whatever the quote has (its inherited starting point).
      const quoteSrp = selectedQuote?.specialRefinisherPay;
      setSrpEnabled(!!quoteSrp?.enabled);
      setSrpAmount(quoteSrp?.amount || null);
      setSrpPayType(quoteSrp?.payType || null);
      setOpenInvoiceOptions(false);
    } else {
      setOpenInvoiceOptions(true);
    }
  };

  const closeModal = () => {
    closeQuoteModal("");
  };

  const finishPostCreate = (args) => {
    // If this graduation created a calendar event for a known customer, offer to
    // clear that customer's OTHER tentative placeholders FIRST. The refresh /
    // email / close steps run when the cleanup modal closes. Gated on
    // createCalEvent: if office unchecked "create calendar event", nothing
    // gained a doc on the calendar, so skip the prompt (R4).
    if (createCalEvent && selectedQuote?.customerId) {
      pendingFinishRef.current = args;
      setCleanupOpen(true);
      return;
    }
    proceedAfterCreate(args);
  };

  const proceedAfterCreate = ({ newInvoiceId, openEmailComposer }) => {
    if (queryClient) {
      queryClient();
    }
    if (openEmailComposer) {
      emailObjHandler({
        type: "invoiceTemplate",
        invoiceLink: `https://bigginsdoorrefinishing.com/client/view-invoice/${newInvoiceId}`,
      });

      const id = setTimeout(() => {
        changeParentForEmailModal(true);
      }, 700);

      timeoutIdRef.current = id;
    }
    closeModal();
  };

  const handleCyclePromptClose = () => {
    setCyclePromptTaskId(null);
    const pending = pendingPostCreateRef.current;
    pendingPostCreateRef.current = null;
    if (pending) {
      finishPostCreate(pending);
    }
  };

  const createInvoice = async (setEmailObj) => {
    if (invoice?.jobDate?.length === 0) {
      if (handleSnack) {
        handleSnack("Please select job date", 3500);
      }
      setDateErr(true);
      return;
    }

    setDateErr(false);

    const srpValidationError = validateSpecialRefinisherPay(srpEnabled, srpAmount);
    if (srpValidationError) {
      if (handleSnack) handleSnack(srpValidationError, 4000);
      return;
    }

    const { data } = await createInvoiceCall({
      variables: {
        quoteId: selectedQuote?.id,
        localEpoch: Date.now().toString(),
        jobScheduledFor: invoice?.jobDate,
        jobEndTime: invoice?.jobEndTime,
        invoicePaid: invoice?.invoicePaid,
        invoiceNote: invoice?.invoiceNote,
        paidBy: invoice?.paidBy,
        skipCalendar: !createCalEvent,
        specialRefinisherPay: buildSpecialRefinisherPayInput(srpEnabled, srpAmount, srpPayType),
      },
    });

    const result = data?.createClientInvoice;
    if (result?.invoiceId) {
      const newInvoiceId = result.invoiceId;
      const isPaid = invoice?.invoicePaid;

      // If already paid, auto-create a pending job form for review
      if (isPaid) {
        try {
          await createPendingJobFormCall({
            variables: {
              invoiceId: newInvoiceId,
              clientId: selectedQuote?.customerId || null,
            },
          });
          handleSnack("Invoice Created — Sent to Job Review", 3500);
        } catch (err) {
          // Job form creation failed but invoice was created successfully
          handleSnack("Invoice Created — Could not auto-create job form", 4000);
        }
      } else {
        handleSnack("Invoice Created", 3500);
      }

      // If cycle inference came back LOW, prompt office to set cycle position
      // before completing the rest of the flow (queryClient refresh, email
      // composer, modal close). The post-create steps run when the cycle modal
      // closes — regardless of whether office confirmed or cancelled.
      if (
        result.cycleConfidence === "low" &&
        result.relatedTaskId
      ) {
        pendingPostCreateRef.current = {
          newInvoiceId,
          openEmailComposer: !!setEmailObj,
        };
        setCyclePromptTaskId(result.relatedTaskId);
        return;
      }

      finishPostCreate({ newInvoiceId, openEmailComposer: !!setEmailObj });
    }

    return;
  };

  const sendQuoteEmail = () => {
    emailObjHandler({
      type: "quoteTemplate",
      invoiceLink: null,
      quoteLink: [
        `https://bigginsdoorrefinishing.com/client/view-quote/${selectedQuote?.id}`,
      ],
    });

    const id = setTimeout(() => {
      changeParentForEmailModal(true);
    }, 700);

    timeoutIdRef.current = id;
    closeModal();
  };

  const invoiceChangeHandler = (event) => {
    let name = event.target.name;
    let value = event.target.value;

    if (name === "invoicePaid") {
      const isPaid = value === "Yes";

      setInvoice((prev) => ({
        ...prev,
        paidBy: isPaid ? "Check" : null,
        invoicePaid: isPaid,
      }));

      return;
    }

    setInvoice((prev) => ({ ...prev, [name]: value }));
  };

  const returnMills = (mills) => {
    if (typeof mills !== "string") return;
    const newStart = parseInt(mills);
    if (isNaN(newStart)) return;

    setDateErr(false);
    setInvoice((prev) => {
      const updated = { ...prev, jobDate: mills };

      if (prev.jobEndTime) {
        const oldEnd = parseInt(prev.jobEndTime);
        if (!isNaN(oldEnd)) {
          // Move end time to the same day as new start, preserving end's HH:MM
          const startDt = new Date(newStart);
          const endDt = new Date(oldEnd);
          const syncedEnd = new Date(
            startDt.getFullYear(),
            startDt.getMonth(),
            startDt.getDate(),
            endDt.getHours(),
            endDt.getMinutes(),
            endDt.getSeconds()
          ).getTime();

          if (newStart >= syncedEnd) {
            // Start is at or after synced end — push end to start + 2 hours
            updated.jobEndTime = String(newStart + TWO_HOURS_MS);
          } else {
            updated.jobEndTime = String(syncedEnd);
          }
        }
      } else {
        // End wasn't set yet — auto-fill it. Mirrors the CreateEventModal
        // prefillDatesFromInvoice pattern: default end to 2:00 PM of the
        // chosen day. If start is at/after 2 PM, fall back to start + 2hrs
        // so end is always after start.
        const startDt = new Date(newStart);
        const defaultEnd = new Date(
          startDt.getFullYear(),
          startDt.getMonth(),
          startDt.getDate(),
          14, 0, 0
        ).getTime();
        if (newStart >= defaultEnd) {
          updated.jobEndTime = String(newStart + TWO_HOURS_MS);
        } else {
          updated.jobEndTime = String(defaultEnd);
        }
      }

      return updated;
    });
  };

  const returnEndMills = (mills) => {
    if (typeof mills !== "string") return;
    const newEnd = parseInt(mills);
    if (isNaN(newEnd)) return;

    setInvoice((prev) => {
      if (prev.jobDate) {
        const curStart = parseInt(prev.jobDate);
        if (!isNaN(curStart) && newEnd <= curStart) {
          // End is before or equal to start — shift start back to end - 1 hour
          return { ...prev, jobDate: String(newEnd - ONE_HOUR_MS), jobEndTime: mills };
        }
      }
      return { ...prev, jobEndTime: mills };
    });
  };

  const preparePDF = async (elId) => {
    const pdf = await invoiceHTMLToPDFBase64("BDR Quote", elId, true);

    if (pdf) {
      const id = setTimeout(() => {
        setDisplayQuotePdf(false);
      }, 2000);
      return () => clearTimeout(id);
    }

    handleSnack("Error preparing print...", 2000);
    setDisplayQuotePdf(false);
    return;
  };

  return (
    <>
    <div
      className="view-quote-modal-container"
      onClick={() => {
        closeModal();
      }}
    >
      <div
        className="view-quote-modal-content"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <button
          className="view-quote-modal-close"
          onClick={() => {
            closeModal();
          }}
        >
          Close
        </button>
        <div className="view-quote-modal-invoice-box">
          <div>
            {allowInvoice && (
              <button
                className="view-quote-modal-btn bdr-grad-admin-btn bdr-border-thin bdr-shadow-subtle"
                onClick={handleOpenInvoiceOptions}
              >
                {openInvoiceOptions ? "Cancel" : "Invoice"}
              </button>
            )}
            <button
              className="view-quote-modal-btn bdr-grad-admin-btn bdr-border-thin bdr-shadow-subtle"
              onClick={sendQuoteEmail}
            >
              Send Quote
            </button>
            <button
              className="view-quote-modal-btn bdr-grad-admin-btn bdr-border-thin bdr-shadow-subtle"
              disabled={displayQuotePdf}
              onClick={() => {
                setDisplayQuotePdf(true);
                return;
              }}
            >
              Print Quote
            </button>
          </div>
          {openInvoiceOptions && (
            <div className="view-quote-modal-invoice-date bdr-grad-admin-btn bdr-border-thin bdr-shadow-subtle">
              {dateErr && (
                <ul>
                  <li>Please select job date!</li>
                </ul>
              )}
              <div className="vq-invoice-options">
                {/* Already Paid? */}
                <div className="vq-field">
                  <span className="vq-field-label">
                    Has invoice already been paid?
                  </span>
                  <div className="vq-radio-row">
                    {["Yes", "No"].map((opt) => (
                      <label key={opt} className="vq-radio-label">
                        <input
                          type="radio"
                          name="invoicePaid"
                          value={opt}
                          checked={
                            invoice.invoicePaid
                              ? opt === "Yes"
                              : opt === "No"
                          }
                          onChange={invoiceChangeHandler}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Paid By */}
                {invoice.invoicePaid && (
                  <div className="vq-field">
                    <span className="vq-field-label">Paid By</span>
                    <div className="vq-radio-row">
                      {["Check", "Card", "Cash"].map((opt) => (
                        <label key={opt} className="vq-radio-label">
                          <input
                            type="radio"
                            name="paidBy"
                            value={opt}
                            checked={invoice.paidBy === opt}
                            onChange={invoiceChangeHandler}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Time inputs */}
                <div className="vq-time-row">
                  <EventDateTimeInput
                    label="Start Time"
                    defaultTime={{ hour: 8, minute: 0, amPm: "AM" }}
                    value={invoice.jobDate}
                    onChange={returnMills}
                  />
                </div>
                <div className="vq-time-row">
                  <EventDateTimeInput
                    label="End Time"
                    defaultTime={{ hour: 2, minute: 0, amPm: "PM" }}
                    value={invoice.jobEndTime}
                    onChange={returnEndMills}
                  />
                </div>

                {/* Create Calendar Event toggle */}
                <div className="vq-switch-row">
                  <Switch
                    checked={createCalEvent}
                    onChange={(e) => setCreateCalEvent(e.target.checked)}
                    size="small"
                    sx={switchSx}
                  />
                  <span className="vq-switch-label">
                    Create Calendar Event
                  </span>
                </div>

                <SpecialRefinisherPayBlock
                  enabled={srpEnabled}
                  setEnabled={setSrpEnabled}
                  amount={srpAmount}
                  setAmount={setSrpAmount}
                  payType={srpPayType}
                  setPayType={setSrpPayType}
                  inheritedHint={srpInheritedHint}
                  theme="dark"
                  maxWidth="550px"
                  items={selectedQuote?.quoteItems}
                />
              </div>

              <BdrInput
                className="bdr-field--w-95"
                label={noteLabel}
                name="invoiceNote"
                value={invoice?.invoiceNote}
                onChange={invoiceChangeHandler}
                onFocus={() => {
                  setNoteLabel("Invoice Note:");
                }}
                onBlur={() => {
                  if (invoice?.invoiceNote?.length <= 0) {
                    setNoteLabel("Invoice Note for Client: (Optional)");
                  }
                }}
                multiline
                rows={3}
              />
              <div className="view-quote-modal-invoice-create-box">
                <button
                  className="view-quote-modal-btn bdr-grad-admin-btn bdr-border-thin bdr-shadow-subtle"
                  onClick={() => {
                    createInvoice(false);
                  }}
                >
                  Create Invoice
                </button>
                <button
                  className="view-quote-modal-btn bdr-grad-admin-btn bdr-border-thin bdr-shadow-subtle"
                  onClick={() => {
                    createInvoice(true);
                  }}
                >
                  Create Invoice & Send Email
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="view-quote-modal-resized">
          <ClientModalInvoiceQuotePage
            displayPdf={displayQuotePdf}
            quoteObj={selectedQuote}
          />
          {viewHistory?.length > 0 && (
            <ul className="view-quote-history-ul">
              {viewHistory?.map((item, index) => {
                return (
                  <li key={`quote-history-view-item-${index}`}>{`View ${
                    viewHistory?.length - index
                  } - ${BAGroupTimeFormat(
                    item?.timestamp,
                    true,
                    true,
                    false
                  )}`}</li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
    {/* Kept OUTSIDE the click-to-close container: both portal to document.body,
        and React-tree bubbling from portals would otherwise hit the container's
        onClick and close the whole quote modal on any click inside them. */}
    <CycleConfirmationModal
      open={!!cyclePromptTaskId}
      taskId={cyclePromptTaskId}
      onClose={handleCyclePromptClose}
    />
    <PlaceholderCleanupModal
      open={cleanupOpen}
      clientId={selectedQuote?.customerId}
      onClose={() => {
        setCleanupOpen(false);
        const pending = pendingFinishRef.current;
        pendingFinishRef.current = null;
        proceedAfterCreate(
          pending || { newInvoiceId: null, openEmailComposer: false }
        );
      }}
    />
    </>
  );
};

export default ViewClientQuoteModal;
